import { useEffect, useState, useCallback } from 'react';
import { ordersAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, CreditCard, Wallet, Gift } from 'lucide-react';

const STATUS_LABELS = {
  pending: { label: 'En attente', className: 'bg-blue-500' },
  preparing: { label: 'En préparation', className: 'bg-amber-500' },
  ready: { label: 'Prête', className: 'bg-blue-500' },
  delivered: { label: 'Livrée', className: 'bg-gray-500' },
  cancelled: { label: 'Annulée', className: 'bg-red-500' }
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await ordersAPI.getAll(params);
      setOrders(response.data);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderPaymentBadge = (order) => {
    if (order.payment_method === 'free') {
      return (
        <Badge variant="outline" className="bg-violet-100 text-violet-700 border-violet-200">
          <Gift className="w-3 h-3 mr-1" />
          Gratuite
        </Badge>
      );
    }

    if (order.payment_method === 'card') {
      return (
        <Badge variant="outline" className={order.payment_status === 'paid' ? 'bg-blue-100 text-violet-700 border-blue-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}>
          <CreditCard className="w-3 h-3 mr-1" />
          {order.payment_status === 'paid' ? 'CB payée' : 'CB en attente'}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className={order.cash_to_collect ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-slate-100 text-slate-700 border-slate-200'}>
        <Wallet className="w-3 h-3 mr-1" />
        {order.cash_to_collect ? 'Espèces à récupérer' : (order.payment_status === 'paid' ? 'Espèces récupérées' : 'Espèces')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
            Commandes
          </h1>
          <p className="text-gray-500">Historique des commandes</p>
        </div>

        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="orders-filter">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="preparing">En préparation</SelectItem>
            <SelectItem value="ready">Prêtes</SelectItem>
            <SelectItem value="delivered">Livrées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Table</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Articles</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Total</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Statut</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Paiement</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const statusConfig = STATUS_LABELS[order.status] || STATUS_LABELS.pending;

                  return (
                    <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <Badge variant="outline">Table {order.table_number}</Badge>
                      </td>

                      <td className="py-3 px-4">
                        <div className="text-sm">
                          {order.items?.map((item, idx) => (
                            <span key={idx}>
                              {item.quantity}x {item.name}
                              {idx < order.items.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="py-3 px-4 font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {order.total_price?.toFixed(2)}€
                      </td>

                      <td className="py-3 px-4">
                        <Badge className={statusConfig.className}>
                          {statusConfig.label}
                        </Badge>
                      </td>

                      <td className="py-3 px-4">
                        {renderPaymentBadge(order)}
                      </td>

                      <td className="py-3 px-4 text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {orders.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              Aucune commande trouvée
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
